import {
	Camera,
	CameraOff,
	Mic,
	MicOff,
	Monitor,
	MonitorOff,
	Pause,
	Play,
	Radio,
	Square,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useStore } from "zustand";
import { Button } from "@/components/ui/button";
import { useCameraPermission } from "@/hooks/use-camera-permission";
import { useMicrophonePermission } from "@/hooks/use-microphone-permission";
import { useScreenShare } from "@/hooks/use-screen-share";
import { authStore } from "@/lib/stores/auth-store";
import { coreStore } from "@/lib/stores/core-store";
import { uiStore } from "@/lib/stores/ui-store";
import {
	BroadcastReactionOverlay,
	BroadcastReactions,
} from "./broadcast-reactions";
import { BroadcastVideoGrid } from "./broadcast-video-grid";
import { ScreenSourcePicker } from "./screen-source-picker";
import { VolumeControl } from "./volume-control";

const BROADCAST_AUDIO_CONSTRAINTS: MediaTrackConstraints = {
	echoCancellation: false,
	noiseSuppression: false,
	autoGainControl: false,
};

type PipBroadcastContentProps = {
	hideHeader?: boolean;
};

export function PipBroadcastContent({ hideHeader }: PipBroadcastContentProps) {
	const containerRef = useRef<HTMLDivElement>(null);
	const [container, setContainer] = useState<HTMLDivElement | null>(null);
	const user = useStore(authStore, (state) => state.user);
	const broadcast = useStore(coreStore, (state) => state.broadcast);
	const pauseBroadcast = useStore(coreStore, (state) => state.pauseBroadcast);
	const resumeBroadcast = useStore(coreStore, (state) => state.resumeBroadcast);
	const stopBroadcast = useStore(coreStore, (state) => state.stopBroadcast);
	const broadcastRoom = useStore(uiStore, (state) => state.broadcastRoom);
	const setBroadcastRoom = useStore(uiStore, (state) => state.setBroadcastRoom);
	const volume = useStore(uiStore, (state) => state.broadcastVolume);
	const broadcastControlSettings = useStore(
		uiStore,
		(state) => state.broadcastControlSettings
	);
	const setBroadcastControlSettings = useStore(
		uiStore,
		(state) => state.setBroadcastControlSettings
	);
	const navigate = useStore(uiStore, (state) => state.navigate);

	const { requestPermission: requestMicPermission } = useMicrophonePermission();
	const { requestPermission: requestCameraPermission } = useCameraPermission();

	const {
		toggleScreenShare,
		handleElectronScreenShare,
		screenPickerOpen,
		setScreenPickerOpen,
		screenSources,
	} = useScreenShare({
		localParticipant: broadcastRoom?.localParticipant,
		sentryContext: "pip_broadcast_screen_share",
		onScreenShareChange: (enabled) =>
			setBroadcastControlSettings({ isScreenShareEnabled: enabled }),
	});

	const isBroadcaster = user?.uid === broadcast?.startedBy;
	const isMicEnabled = broadcastControlSettings.isMicEnabled;
	const isCameraEnabled = broadcastControlSettings.isCameraEnabled;
	const isScreenShareEnabled = broadcastControlSettings.isScreenShareEnabled;
	const isLive = broadcast?.status === "live";

	const toggleMic = async () => {
		if (!broadcastRoom) return;
		const newState = !isMicEnabled;
		if (newState) {
			const granted = await requestMicPermission();
			if (!granted) return;
		}
		setBroadcastControlSettings({ isMicEnabled: newState });
		await broadcastRoom.localParticipant.setMicrophoneEnabled(
			newState,
			BROADCAST_AUDIO_CONSTRAINTS
		);
	};

	const toggleCamera = async () => {
		if (!broadcastRoom) return;
		const newState = !isCameraEnabled;
		if (newState) {
			const granted = await requestCameraPermission();
			if (!granted) return;
		}
		setBroadcastControlSettings({ isCameraEnabled: newState });
		await broadcastRoom.localParticipant.setCameraEnabled(newState);
	};

	const handlePause = async () => {
		try {
			if (broadcastRoom) {
				if (isScreenShareEnabled) {
					await broadcastRoom.localParticipant.setScreenShareEnabled(false);
					setBroadcastControlSettings({ isScreenShareEnabled: false });
				}
				const localTracks = Array.from(
					broadcastRoom.localParticipant.trackPublications.values()
				);
				for (const publication of localTracks) {
					if (publication.track) {
						publication.track.stop();
					}
				}
				broadcastRoom.disconnect();
				setBroadcastRoom(null);
			}
			await pauseBroadcast();
			toast.info("Broadcast paused");
			navigate({ type: "broadcastControls" });
		} catch (error) {
			console.error("Failed to pause broadcast:", error);
			toast.error("Failed to pause broadcast");
		}
	};

	const handleResume = async () => {
		try {
			await resumeBroadcast();
		} catch (error) {
			console.error("Failed to resume broadcast:", error);
			toast.error("Failed to go live");
		}
	};

	const handleStop = async () => {
		try {
			if (broadcastRoom) {
				const localTracks = Array.from(
					broadcastRoom.localParticipant.trackPublications.values()
				);
				for (const publication of localTracks) {
					if (publication.track) {
						publication.track.stop();
					}
				}
				broadcastRoom.disconnect();
				setBroadcastRoom(null);
			}
			await stopBroadcast();
			toast.info("Broadcast ended");
			navigate({ type: "broadcastControls" });
		} catch (error) {
			console.error("Failed to stop broadcast:", error);
			toast.error("Failed to stop broadcast");
		}
	};

	useEffect(() => {
		setContainer(containerRef.current);
	}, []);

	const [audioReady, setAudioReady] = useState(false);

	// Satisfies the browser autoplay policy before audio tracks render.
	useEffect(() => {
		if (!broadcastRoom) {
			setAudioReady(false);
			return;
		}
		let cancelled = false;
		broadcastRoom
			.startAudio()
			.then(() => {
				if (!cancelled) setAudioReady(true);
			})
			.catch(() => {
				if (!cancelled) setAudioReady(true);
			});
		return () => {
			cancelled = true;
		};
	}, [broadcastRoom]);

	if (!(broadcast && broadcastRoom && broadcast.status === "live")) {
		return null;
	}

	const participantCount = broadcastRoom.remoteParticipants.size + 1;

	return (
		<>
			<ScreenSourcePicker
				onOpenChange={setScreenPickerOpen}
				onSelect={handleElectronScreenShare}
				open={screenPickerOpen}
				sources={screenSources}
			/>
			<div className="flex h-full w-full flex-col bg-card" ref={containerRef}>
				{!hideHeader && (
					<div className="flex shrink-0 items-center justify-between border-b bg-card px-3 py-2">
						<div className="flex items-center gap-2">
							<Radio className="size-4 text-green-500" />
							<div className="flex flex-col">
								<span className="font-medium text-foreground text-sm">
									{broadcast.title}
								</span>
								<span className="text-muted-foreground text-xs">
									{participantCount} viewer{participantCount !== 1 ? "s" : ""}
								</span>
							</div>
							<span className="rounded bg-green-500/20 px-1.5 py-0.5 font-medium text-green-500 text-xs">
								LIVE
							</span>
						</div>
						<div className="flex items-center gap-1">
							<BroadcastReactions container={container} />
							<VolumeControl container={container} />
						</div>
					</div>
				)}
				<div className="relative w-full flex-1 overflow-hidden bg-muted">
					{audioReady ? (
						<>
							<BroadcastVideoGrid variant="compact" volume={volume} />
							<BroadcastReactionOverlay />
						</>
					) : (
						<div className="flex h-full items-center justify-center">
							<Radio className="size-8 animate-pulse text-green-500" />
						</div>
					)}
				</div>
				{isBroadcaster && (
					<div className="flex shrink-0 items-center justify-center gap-1.5 border-t bg-card/50 px-2 py-2 backdrop-blur-sm">
						{isLive ? (
							<Button
								className="h-8 rounded-full px-3 text-xs"
								onClick={handlePause}
								size="sm"
								variant="secondary"
							>
								<Pause className="mr-1 size-3" />
								Pause
							</Button>
						) : (
							<Button
								className="h-8 rounded-full px-3 text-xs"
								onClick={handleResume}
								size="sm"
								variant="destructive"
							>
								<Play className="mr-1 size-3" />
								Live
							</Button>
						)}

						<Button
							className="size-8 rounded-full p-0"
							onClick={toggleMic}
							size="sm"
							variant="ghost"
						>
							{isMicEnabled ? (
								<Mic className="size-4" />
							) : (
								<MicOff className="size-4" />
							)}
						</Button>

						<Button
							className="size-8 rounded-full p-0"
							onClick={toggleCamera}
							size="sm"
							variant="ghost"
						>
							{isCameraEnabled ? (
								<Camera className="size-4" />
							) : (
								<CameraOff className="size-4" />
							)}
						</Button>

						<Button
							className="size-8 rounded-full p-0"
							onClick={handleStop}
							size="sm"
							variant="destructive"
						>
							<Square className="size-3" />
						</Button>

						<Button
							className="size-8 rounded-full p-0"
							onClick={toggleScreenShare}
							size="sm"
							variant="ghost"
						>
							{isScreenShareEnabled ? (
								<MonitorOff className="size-4" />
							) : (
								<Monitor className="size-4" />
							)}
						</Button>
					</div>
				)}
			</div>
		</>
	);
}
